import { useRef, useState } from "react";
import { ChevronsLeftRight, Check, X } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";

const BEFORE = [
  "37 mensagens sem resposta no fim do dia",
  "6 horários vagos na agenda de amanhã",
  "Cliente desistiu às 21h12 — sem retorno",
  "Equipe dividida entre balcão e WhatsApp",
];

const AFTER = [
  "Resposta em segundos, a qualquer hora*",
  "Agenda com confirmação automática",
  "Cliente das 21h12 agendado para amanhã",
  "Equipe 100% focada no atendimento presencial",
];

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <section id="antes-depois" data-testid="before-after-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <ChapterHeading
          index="07"
          eyebrow="A transformação"
          lines={["Arraste e veja a sua", "recepção antes e depois."]}
          sub="O mesmo negócio. A mesma equipe. A diferença é quem cuida das conversas."
        />

        <Reveal delay={0.15} className="mt-16">
          <div
            ref={ref}
            data-testid="before-after-slider"
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture(e.pointerId);
              update(e.clientX);
            }}
            onPointerMove={(e) => dragging.current && update(e.clientX)}
            onPointerUp={() => (dragging.current = false)}
            onPointerCancel={() => (dragging.current = false)}
            className="relative h-[480px] cursor-ew-resize touch-none select-none overflow-hidden rounded-[32px] border border-[var(--line)] sm:h-[420px]"
          >
            {/* ANTES */}
            <div className="absolute inset-0 bg-[#1C2B33] p-8 sm:p-14">
              <span className="rounded-full border border-red-400/40 px-4 py-1.5 font-mono2 text-[11px] uppercase tracking-[0.3em] text-red-300">
                Antes
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold text-white/90 sm:text-3xl">
                Sua recepção hoje
              </h3>
              <ul className="mt-8 space-y-4">
                {BEFORE.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/60 sm:text-base">
                    <X className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* DEPOIS */}
            <div
              className="absolute inset-0 bg-petrol p-8 sm:p-14"
              style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{ background: "radial-gradient(circle at 80% 20%, rgba(39,179,176,0.35), transparent 60%)" }}
              />
              <div className="relative ml-auto max-w-md text-right">
                <span className="rounded-full border border-turquoise/50 px-4 py-1.5 font-mono2 text-[11px] uppercase tracking-[0.3em] text-turquoise">
                  Depois
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-white sm:text-3xl">
                  Com a CliniAtiva
                </h3>
                <ul className="mt-8 space-y-4">
                  {AFTER.map((a) => (
                    <li key={a} className="flex flex-row-reverse items-start gap-3 text-sm text-sage sm:text-base">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-turquoise" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* HANDLE */}
            <div
              data-testid="before-after-handle"
              className="absolute inset-y-0 z-10"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute inset-y-0 -left-px w-0.5 bg-turquoise shadow-[0_0_20px_rgba(39,179,176,0.8)]" />
              <div className="absolute top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-turquoise text-petrol shadow-[0_10px_30px_rgba(39,179,176,0.5)]">
                <ChevronsLeftRight className="h-6 w-6" />
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-[var(--muted)]">
            * Estimativa ilustrativa. Resultados variam conforme a operação de cada negócio.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
